import { throttle } from 'throttle-debounce';
import { keepContentAlignedBelowKicker } from '../../../ts/helper';

/** Selector for any instance of this _Friendation_ component */
const selector =
  '[is="block-accordion"], [is="columns-tariffs"], [is="block-image-interactive"]';

/**
 * Friendation component
 */
export const blockAccordion = () => {
  /** Instances of this Friendation component */
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll(selector)
  );

  components.forEach((component) => {
    const handleAcc = () => {
      const triggers: HTMLButtonElement[] = Array.from(
        component.querySelectorAll('.panel-trigger')
      );

      let currentIndex = 0;

      triggers.forEach((trigger) => {
        // toggle aria-expanded attribute
        trigger.addEventListener('click', (e: MouseEvent) => {
          e.preventDefault();

          const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

          // close all other panels first
          triggers.forEach((other) => {
            if (other !== trigger) {
              other.setAttribute('aria-expanded', 'false');
            }
          });

          trigger.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
        });

        // listen for arrow keys to go the the prev/next element
        const onArrowKey = (e: KeyboardEvent) => {
          if (['ArrowDown', 'ArrowUp'].includes(e.code)) {
            e.preventDefault();

            currentIndex =
              triggers.findIndex((x) => x === document.activeElement) || 0;

            let nextIndex = currentIndex + 1;
            if (nextIndex >= triggers.length) nextIndex = 0;

            let prevIndex = currentIndex - 1;
            if (prevIndex < 0) prevIndex = triggers.length - 1;

            if (currentIndex !== undefined) {
              if (e.code === 'ArrowDown') {
                setTimeout(() => {
                  triggers[nextIndex]?.focus();
                }, 100);
              }

              if (e.code === 'ArrowUp') {
                setTimeout(() => {
                  triggers[prevIndex]?.focus();
                }, 100);
              }
            }
          }
        };
        trigger.addEventListener('keydown', onArrowKey);

        const update = () => {
          const refEl = <HTMLElement>(
            component.querySelector(
              `.panel-content[id="${trigger.getAttribute('aria-controls')}"]`
            )
          );

          const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

          if (isExpanded) {
            refEl.style.maxHeight = `${refEl.scrollHeight}px`;
          } else {
            refEl.style.maxHeight = `0px`;
          }

          refEl.inert = !isExpanded;
        };

        const callback = (mutationList: MutationRecord[]) => {
          for (const mutation of mutationList) {
            if (mutation.type === 'attributes') {
              update();
            }
          }
        };

        // Create an observer instance linked to the callback function
        const observer = new MutationObserver(callback);

        // Start observing the target node for configured mutations
        observer.observe(trigger, { attributes: true });

        const onResize = throttle(100, () => {
          update();
        });

        window.addEventListener('resize', onResize);

        // Recalculate when the panel content changes height (e.g. skip links of a table becoming visible on focus)
        const panelInner = component.querySelector(
          `.panel-content[id="${trigger.getAttribute('aria-controls')}"] > .panel-inner`
        );
        if (panelInner) new ResizeObserver(onResize).observe(panelInner);

        update();
      });
    };

    const handleLayout = () => {
      keepContentAlignedBelowKicker(
        component.querySelector('.panelWrapper'),
        component.querySelector('.text'),
        1280
      );
    };

    handleAcc();
    handleLayout();
  });
};
